// InvoiceList.jsx
import React, { useEffect, useState, useMemo } from 'react';
import T from '../../components/common/T';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosConfig';
import Price from "../../components/common/Price";
import DataTable from "../../components/common/DataTables.jsx";
import TableActionButtons from "../../components/common/TableActionButtons";
import { TopPrimaryButton } from "../../components/common/StandardButtons";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from "../../components/common/PageLayout";
import useModuleFilters from "../../hooks/useModuleFilters";
import {
  DocumentTextIcon,
  DocumentDuplicateIcon, CheckCircleIcon, ExclamationTriangleIcon, ClockIcon
} from "@heroicons/react/24/outline";

export default function InvoiceList() {
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateSort, setDateSort] = useState('');
  const [error, setError] = useState(null);

  const {
    showFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
  } = useModuleFilters("invoices", {
    statuses: [],
    projects: [],
  });

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await api.get("/accounting/invoices");
      setInvoices(res.data || []);
      setError(null);
    } catch {
      setError("Failed to load invoices. Check backend connection.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvoices(); }, []);

  const filteredInvoices = useMemo(() => {
    let result = [...invoices];
    if (filter && filter !== 'All') {
      result = result.filter(inv => (inv.status || '').toLowerCase() === filter.toLowerCase());
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(inv =>
        (inv.invoice_number || '').toLowerCase().includes(s) ||
        (inv.client_name || '').toLowerCase().includes(s) ||
        (inv.project_name || '').toLowerCase().includes(s)
      );
    }
    if (dateSort === 'newest') {
      result.sort((a, b) => new Date(b.invoice_date || 0) - new Date(a.invoice_date || 0));
    } else if (dateSort === 'oldest') {
      result.sort((a, b) => new Date(a.invoice_date || 0) - new Date(b.invoice_date || 0));
    }
    return result;
  }, [invoices, filter, searchTerm, dateSort]);

  // ── Stats from ALL invoices ──────────────────────────────────────────────
  const stats = useMemo(() => [
    {
      label: "Total Invoices",
      value: invoices.length,
      icon: DocumentDuplicateIcon,
      variant: "default",
      isActive: filter === "All" || !filter,
      onClick: () => setFilter("All"),
    },
    {
      label: "Paid",
      value: invoices.filter(i => (i.status || '').toLowerCase() === 'paid').length,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: filter === "Paid",
      onClick: () => setFilter(f => f === 'Paid' ? 'All' : 'Paid'),
    },
    {
      label: "Overdue",
      value: invoices.filter(i => (i.status || '').toLowerCase() === 'overdue').length,
      icon: ExclamationTriangleIcon,
      variant: "red",
      isActive: filter === "Overdue",
      onClick: () => setFilter(f => f === 'Overdue' ? 'All' : 'Overdue'),
    },
    {
      label: "Draft",
      value: invoices.filter(i => (i.status || '').toLowerCase() === 'draft').length,
      icon: ClockIcon,
      variant: "gray",
      isActive: filter === "Draft",
      onClick: () => setFilter(f => f === 'Draft' ? 'All' : 'Draft'),
    },
  ], [invoices, filter]);

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'paid': return 'bg-green-50 dark:bg-emerald-950/40 text-green-700 dark:text-emerald-400 border-green-200 dark:border-emerald-800/60';
      case 'sent': return 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/60';
      case 'partially_paid': return 'bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-400 border-teal-200 dark:border-teal-800/60';
      case 'overdue': return 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/60';
      case 'void': return 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 border-gray-200 dark:border-slate-700';
      default: return 'bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-200 dark:border-slate-700';
    }
  };

  const columns = [
    { header: "Invoice #", accessor: "invoice_number", sortable: true, render: (inv) => <span className="font-bold text-blue-600 dark:text-blue-400">{inv.invoice_number}</span> },
    { header: "Client", accessor: "client_name", sortable: true, render: (inv) => inv.client_name ? <span className="font-medium text-gray-800 dark:text-white">{inv.client_name}</span> : <span className="text-gray-400 dark:text-slate-500 italic text-[11px]"><T>No Client</T></span> },
    { header: "Project", accessor: "project_name", sortable: true, render: (inv) => <span className="text-gray-700 dark:text-slate-300">{inv.project_name || '-'}</span> },
    { header: "Date", accessor: "invoice_date", sortable: true, render: (inv) => <span className="text-gray-600 dark:text-slate-400">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '-'}</span> },
    { header: "Amount", accessor: "grand_total", align: "right", sortable: true, render: (inv) => <span className="font-bold text-gray-900 dark:text-white"><Price amount={Number(inv.grand_total || 0)} /></span> },
    {
      header: "Status", accessor: "status", align: "center", sortable: true,
      render: (inv) => (
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${getStatusColor(inv.status)}`}>
          {inv.status}
        </span>
      )
    },
    {
      header: "Action", align: "right",
      render: (inv) => (
        <TableActionButtons
          onView={(e) => { e.stopPropagation(); navigate(`/admin/accounting/invoices/${inv.id}`); }}
          viewTitle="View Invoice"
        />
      )
    }
  ];

  return (
    <PageLayout>
      {/* Header */}
      <PageHeader
        icon={DocumentTextIcon}
        iconColor="text-[#E8612C]"
        title="Customer Invoices"
        subtitle="Manage billing, receivables, and payment tracking"
        onRefresh={fetchInvoices}
        loading={loading}
      >
        <TopPrimaryButton
          onClick={() => navigate('/admin/accounting/invoices/new')}
          label="Create Invoice"
        />
      </PageHeader>

      {/* Mini Dashboard Stats */}
      <StatsGrid stats={stats} cols={4} />

      {/* Unified Search & Filters Toolbar */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search invoices, clients, projects..."
        tabs={['All', 'Draft', 'Sent', 'Paid', 'Overdue', 'Void']}
        activeTab={filter}
        onTabChange={setFilter}
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      />

      {/* Collapsible Filter Panel */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        onClear={() => { setFilter('All'); setDateSort(''); setSearchTerm(''); }}
      >
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Status</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            disabled={loadingFilters}
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="All"><T>All Statuses</T></option>
            <option value="Draft"><T>Draft</T></option>
            <option value="Sent"><T>Sent</T></option>
            <option value="Paid"><T>Paid</T></option>
            <option value="Overdue"><T>Overdue</T></option>
            <option value="Void"><T>Void</T></option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1"><T>Sort By Date</T></label>
          <select
            value={dateSort}
            onChange={e => setDateSort(e.target.value)}
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value=""><T>Default</T></option>
            <option value="newest"><T>Newest First</T></option>
            <option value="oldest"><T>Oldest First</T></option>
          </select>
        </div>
      </FilterPanel>

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-rose-950/40 text-red-700 dark:text-rose-400 p-3 rounded-lg border border-red-200 dark:border-rose-900/60 text-xs font-bold">
          ⚠️ {error}
        </div>
      )}

      {/* Table Card */}
      <TableCard>
        <DataTable data={filteredInvoices} columns={columns} emptyMessage={`No invoices match current filters.`} />
      </TableCard>
    </PageLayout>
  );
}